import { Modal } from 'antd';
import cls from 'classnames';
import Cropper from 'cropperjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import styles from './index.module.scss';
import avatarCropSampleImgX from '../../../assets/ImageCreation/avatar-crop-sample-x.png';
import avatarCropSampleImg from '../../../assets/ImageCreation/avatar-crop-sample.png';
import closeIcon from '../../../assets/ImageCreation/close.svg';
import { useAvatarSafeCheck } from '../../../hooks/useSafeCheck';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  setImgConfig,
  setImgPreviewExtra,
  setCropingImgUrl,
  Global_Image_Memory,
} from '../../../state/image';
import { track } from '../../../track';
import { isTalkie } from '../../../utils';
import { uploadImageFile } from '../../../utils/upload';
import StepLoading from '../../Components/Loading/step';
import { LocalImage } from '../../Image';

interface ImageEditModalProps {
  show: boolean;
  setShow: (value: boolean) => void;
  cdnPrefix: string;
  source: string;
}

interface ImageSelectProps {
  cdnPrefix: string;
}

const CropAvatar = (
  props: ImageSelectProps & { corpRef: React.MutableRefObject<Cropper | null> }
) => {
  const { cropingImgUrl, imgConfig } = useAppSelector((state) => state.image);
  const ref = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (ref.current && cropingImgUrl) {
      const file = Global_Image_Memory[cropingImgUrl];
      if (file) {
        const objectUrl = URL.createObjectURL(file);
        ref.current.src = objectUrl;

        return () => {
          URL.revokeObjectURL(objectUrl);
        };
      }
    }
  }, [cropingImgUrl]);

  const handleCrop = useCallback(() => {
    const image = ref.current;

    if (image) {
      const cropper = new Cropper(image as HTMLImageElement, {
        aspectRatio: 1 / 1,
        viewMode: 2,
        dragMode: 'move',
        autoCropArea: 0.6, // 自动裁剪区域为60%
        background: false,
        center: false,
        ready() {
          if (imgConfig.position?.top !== undefined) {
            cropper.setData({
              x: imgConfig.position.left,
              y: imgConfig.position.top,
              height: imgConfig.position.bottom! - imgConfig.position.top!,
              width: imgConfig.position.right! - imgConfig.position.left!,
            });
          }
        },
      });
      props.corpRef.current = cropper;
    }
  }, [
    imgConfig.position?.bottom,
    imgConfig.position?.left,
    imgConfig.position?.right,
    imgConfig.position?.top,
    props.corpRef,
  ]);

  useEffect(() => {
    handleCrop();
  }, [handleCrop]);

  return (
    <div className={styles.cropAvatar}>
      <img
        ref={ref}
        style={{
          width: '100%',
          height: '100%',
          visibility: 'hidden',
        }}
        alt="avatar demo"
      />
      <div id="cropped-image"></div>
    </div>
  );
};

function ImageEdit(
  props: ImageSelectProps & {
    onCancel: () => void;
    source: string;
  }
) {
  const { cdnPrefix, onCancel, source } = props;
  const { userId } = useAppSelector((state) => state.user.authData);
  const corpAvatarRef = useRef<Cropper | null>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { safeCheck } = useAvatarSafeCheck();
  const [loading, setLoading] = useState(false);

  const handleOK = useCallback(async () => {
    const corpAvatar = corpAvatarRef.current;

    if (!corpAvatar) return;
    setLoading(true);
    const cropValue = {
      top: 0,
      bottom: Math.floor(corpAvatar?.getData()?.height),
      left: 0,
      right: Math.floor(corpAvatar?.getData()?.width),
    };

    dispatch(
      setImgConfig({
        position: cropValue,
      })
    );

    try {
      const uploadImagePromise = new Promise<string>((resolve, reject) => {
        const canvas = corpAvatar.getCroppedCanvas();
        canvas.toBlob(async function (blob) {
          const file = new File([blob!], 'avatar.jpg', { type: 'image/jpeg' });
          try {
            const res = await uploadImageFile({ file, userId });
            resolve(cdnPrefix + res.pathname);
          } catch (err) {
            reject(err);
          }
        }, 'image/jpeg');
      });

      const avatarUrl = await uploadImagePromise;
      const pass = await safeCheck(avatarUrl);
      if (pass.length === 0 || pass.includes(false)) throw new Error(t('image_not_pass'));
      dispatch(
        setImgPreviewExtra({
          face_img_url: avatarUrl,
        })
      );
      track.send('face_reference_image_confirm_click', source, {
        image_url: avatarUrl,
      });
      setLoading(false);
      onCancel();
    } catch (err: any) {
      toast.error(err.message || err.status_msg);
      dispatch(setCropingImgUrl(''));
      setLoading(false);
    }
    onCancel();
  }, [cdnPrefix, dispatch, onCancel, safeCheck, t, userId, source]);

  return (
    <div className={styles.body}>
      <div className={styles.tip}>
        <LocalImage src={isTalkie ? avatarCropSampleImg : avatarCropSampleImgX} />
        {t('crop_face_reference_tip')}
      </div>
      <div className={styles.content}>
        <CropAvatar corpRef={corpAvatarRef} {...props} />
      </div>
      <div className={styles.btnWrapper}>
        <div className={styles.btn} onClick={onCancel}>
          {t('crop_modal_cancel')}
        </div>
        <div
          className={cls(styles.btn, styles.primary, loading && styles.loading)}
          onClick={handleOK}
        >
          {loading && <StepLoading />}
          {t('crop_modal_apply')}
        </div>
      </div>
    </div>
  );
}

export const ImageCropModal = (props: ImageEditModalProps) => {
  const { show, setShow, source } = props;
  const { t } = useTranslation();
  const onCancel = useCallback(() => setShow(false), [setShow]);

  return (
    <Modal
      classNames={{ content: styles.modal }}
      centered
      open={show}
      onCancel={onCancel}
      footer={null}
      closeIcon={null}
      width={848}
      destroyOnClose
    >
      <div className={styles.header}>
        {t('crop_face_reference')}
        <LocalImage src={closeIcon} onClick={onCancel} />
      </div>
      <ImageEdit {...props} onCancel={onCancel} source={source} />
    </Modal>
  );
};
